import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function AdminScheduler() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(null);
  const [flash, setFlash] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/scheduler");
      setData(data);
    } catch (e) {
      setData({ error: e.response?.data?.detail || e.message });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, []);

  const runNow = async (region) => {
    setRunning(region || "ALL");
    setFlash("");
    try {
      const { data } = await api.post("/admin/digest/run-now", { region: region || null });
      setFlash(`Dispatched · region ${data.region} · sent ${data.sent} · skipped ${data.skipped} · listings ${data.listings_count}`);
      await load();
    } catch (e) {
      setFlash("Run failed: " + (e.response?.data?.detail || e.message));
    } finally {
      setRunning(null);
    }
  };

  if (!user?.is_admin) {
    return (
      <main className="max-w-md mx-auto px-6 py-24 text-center">
        <div className="fleuron mb-6">❦</div>
        <h1 className="font-display text-4xl">The stewards only.</h1>
        <p className="font-body text-ink-iron mt-4">This room is closed to wandering souls.</p>
        <Link to="/dashboard" className="wax-button mt-6 inline-block">Back to dashboard</Link>
      </main>
    );
  }

  const fmt = (iso) => {
    if (!iso) return "—";
    const d = new Date(iso);
    return d.toLocaleString(undefined, { weekday: "short", year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit", timeZoneName: "short" });
  };

  return (
    <main className="max-w-5xl mx-auto px-6 sm:px-10 py-12">
      <Link to="/dashboard" className="label-overline hover:text-wax-red">← back to dashboard</Link>
      <div className="mt-6 mb-10">
        <p className="label-overline">Admin · steward's room</p>
        <h1 className="font-display text-5xl mt-2">The Scheduler</h1>
        <p className="font-body text-ink-iron mt-2">Internal alarm clock. Two regional sends of the weekly Letter from the Estate.</p>
      </div>

      {loading && !data ? (
        <p className="marginalia">Consulting the ledger…</p>
      ) : data?.error ? (
        <p className="text-wax-red italic">{data.error}</p>
      ) : (
        <>
          {/* Status */}
          <div className="parchment-card mb-6" data-testid="scheduler-status">
            <div className="flex items-center justify-between">
              <div>
                <p className="label-overline">Scheduler status</p>
                <p className="font-display text-3xl mt-1">
                  {data.scheduler_enabled ? (
                    <span className="text-moss">Running</span>
                  ) : (
                    <span className="text-wax-red">Stopped</span>
                  )}
                </p>
              </div>
              <button onClick={load} className="ghost-button" data-testid="scheduler-refresh">Refresh</button>
            </div>
            <p className="font-body text-sm text-ink-marginalia italic mt-3">Server time: {fmt(data.now_utc)}</p>
          </div>

          {/* Jobs */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            {data.jobs.map((j) => {
              const region = j.id.includes("usa") ? "USA" : j.id.includes("uk") ? "UK" : null;
              return (
                <div key={j.id} className="parchment-card" data-testid={`scheduler-job-${j.id}`}>
                  <p className="label-overline">{region ? `Region · ${region}` : "Job"}</p>
                  <p className="font-display text-2xl mt-2">{j.name}</p>
                  <p className="font-body text-ink-iron mt-3"><strong>Next run:</strong> {fmt(j.next_run_at)}</p>
                  <p className="font-body text-sm text-ink-marginalia mt-2 italic break-words">{j.trigger}</p>
                  {region && (
                    <button
                      data-testid={`scheduler-run-${region.toLowerCase()}`}
                      onClick={() => runNow(region)}
                      disabled={running !== null}
                      className="wax-button mt-4 w-full"
                    >{running === region ? "Sending…" : `Send ${region} now`}</button>
                  )}
                </div>
              );
            })}
          </div>

          <div className="parchment-card mb-6">
            <button
              data-testid="scheduler-run-all"
              onClick={() => runNow(null)}
              disabled={running !== null}
              className="ghost-button w-full"
            >
              {running === "ALL" ? "Dispatching to all opted-in subscribers…" : "Send to ALL subscribers (ignore region)"}
            </button>
            {flash && <p className="font-body text-sm italic mt-3 text-moss">{flash}</p>}
          </div>

          {/* Stats */}
          <div className="parchment-card mb-6" data-testid="scheduler-stats">
            <p className="label-overline">The post — who is on the list</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
              <div>
                <p className="font-display text-3xl">{data.stats.total_users}</p>
                <p className="font-body text-sm text-ink-marginalia">Total users</p>
              </div>
              <div>
                <p className="font-display text-3xl">{data.stats.unsubscribed}</p>
                <p className="font-body text-sm text-ink-marginalia">Unsubscribed</p>
              </div>
              {Object.entries(data.stats.opt_in_by_region || {}).map(([region, count]) => (
                <div key={region}>
                  <p className="font-display text-3xl">{count}</p>
                  <p className="font-body text-sm text-ink-marginalia">Opted-in · {region}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Recent runs */}
          <div className="parchment-card" data-testid="scheduler-recent-runs">
            <p className="label-overline">Recent dispatches</p>
            <h2 className="font-display text-2xl mt-2 mb-3">The mailroom's ledger</h2>
            {data.recent_runs.length === 0 ? (
              <p className="marginalia">No dispatches yet. The first will be on Monday at 9am local.</p>
            ) : (
              <table className="w-full font-body text-sm">
                <thead>
                  <tr className="text-left text-ink-marginalia">
                    <th className="py-2 pr-3">When</th>
                    <th className="py-2 pr-3">Region</th>
                    <th className="py-2 pr-3">Sent</th>
                    <th className="py-2 pr-3">Skipped</th>
                    <th className="py-2 pr-3">Listings</th>
                    <th className="py-2">Triggered by</th>
                  </tr>
                </thead>
                <tbody>
                  {data.recent_runs.map((r, i) => (
                    <tr key={i} className="border-t border-rule">
                      <td className="py-2 pr-3">{fmt(r.run_at)}</td>
                      <td className="py-2 pr-3">{r.region || "ALL"}</td>
                      <td className="py-2 pr-3">{r.sent}</td>
                      <td className="py-2 pr-3">{r.skipped}</td>
                      <td className="py-2 pr-3">{r.listings_count}</td>
                      <td className="py-2 text-ink-marginalia italic">{r.triggered_by}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}
    </main>
  );
}
